import { Row } from "@tanstack/react-table";

export type FilterableAttribute = {
    key: string;
    label: string;
};

export type ActiveFilter = {
    key: string;
    operator: string;
    value: string;
};

export type QueryBuilderOption = {
    label: string;
    value: string;
};

type Operator = {
    label: string;
    value: string;
};

export type Draft = {
    step: "key" | "operator" | "value";
    key: string | null;
    operator: string | null;
    operatorLabel: string | null;
};

class QueryBuilder {
    private equalHash = "b64cc2760536699c09c33fd0c38b16350e500872";
    private containsHash = "caeb909ae4ff4ee25a0ff0d476946ed256f2aee1";
    private filterSeparator = "\x1F";
    private arrayValueSeparator = "\x1E";

    private operators: Operator[] = [
        { label: "=", value: this.equalHash },
        { label: "contains", value: this.containsHash },
    ];

    private normalize(value: unknown): string {
        return String(value ?? "")
            .toLowerCase()
            .trim();
    }

    public getEqualHash() {
        return this.equalHash;
    }

    public getContainsHash() {
        return this.containsHash;
    }

    public getOperators() {
        return this.operators;
    }

    public getOperatorLabel(operatorValue: string): string {
        return operatorValue === this.equalHash ? "=" : "contains";
    }

    public getAttributeLabel(attributes: FilterableAttribute[], attributeKey: string): string {
        return attributes.find((attr) => attr.key === attributeKey)?.label ?? attributeKey;
    }

    public getOperatorValue(operatorLabel: string): string {
        return this.operators.find((op) => op.label === operatorLabel)?.value ?? operatorLabel;
    }

    public parseFiltersToString(filters: ActiveFilter[]): string {
        return filters
            .map((filter) => {
                const operator = this.getOperatorValue(filter.operator);
                return `${filter.key}${operator}${filter.value}`;
            })
            .join(this.filterSeparator);
    }

    public parseStringToFilters(value: string): ActiveFilter[] {
        return value.split(this.filterSeparator).flatMap((item) => {
            for (const op of this.operators) {
                const idx = item.indexOf(op.value);
                if (idx !== -1) {
                    return [{ key: item.slice(0, idx), operator: op.value, value: item.slice(idx + op.value.length) }];
                }
            }
            return [];
        });
    }

    public filterByQueryMap = <T extends { queryMap?: Record<string, string> }>(
        row: Row<T>,
        columnId: string,
        filterValue: string,
    ): boolean => {
        // The filter operates on queryMap, not on individual column values.
        // Evaluated per (row, column) pair — returning false for all but the first column
        // avoids redundant evaluations; a row passes if any column returns true.
        if (columnId !== row.getAllCells()[0].column.id) {
            return false;
        }

        const filters = this.parseStringToFilters(filterValue);

        if (filters.length === 0 && filterValue.trim()) {
            const term = this.normalize(filterValue);
            return Object.values(row.original.queryMap ?? {}).some((v) => v.includes(term));
        }

        // Known limitation: two filters targeting different fields of the same array item
        // (e.g. packages.versionCode AND packages.package) are evaluated independently
        // against the flat queryMap. A row passes if each value appears anywhere in the
        // array — not necessarily in the same item. This can produce false positives when
        // a device has one app matching the first condition and a different app matching
        // the second. Fixing this requires either indexed queryMap keys or carrying the
        // raw source object alongside, both of which have significant memory trade-offs.
        return this.matchesQueryMap(row.original.queryMap ?? {}, filters);
    };

    public generateQueryMap(obj: unknown, prefix = "", result: Record<string, string> = {}): Record<string, string> {
        if (obj === null || typeof obj !== "object" || obj instanceof Date) {
            if (prefix) {
                result[prefix] = this.normalize(obj);
            }
            return result;
        }

        if (Array.isArray(obj)) {
            const accumulated: Record<string, string[]> = {};
            for (const item of obj) {
                const itemResult = this.generateQueryMap(item, prefix);
                for (const [key, value] of Object.entries(itemResult)) {
                    (accumulated[key] ??= []).push(value);
                }
            }
            for (const [key, values] of Object.entries(accumulated)) {
                result[key] = [...new Set(values)].join(this.arrayValueSeparator);
            }
            return result;
        }

        Object.entries(obj as Record<string, unknown>).forEach(([key, value]) => {
            const path = prefix ? `${prefix}.${key}` : key;
            this.generateQueryMap(value, path, result);
        });

        return result;
    }

    private matchesQueryMap(queryMap: Record<string, string>, filters: ActiveFilter[]): boolean {
        return filters.every((filter) => this.matchesStoredValue(queryMap[filter.key], filter.operator, filter.value));
    }

    private matchesStoredValue(stored: string | undefined, operator: string, value: string): boolean {
        const normalizedValue = this.normalize(value);

        if (operator === this.equalHash) {
            return stored === normalizedValue || stored?.split(this.arrayValueSeparator).includes(normalizedValue) || false;
        }

        if (operator === this.containsHash) {
            const tokens = stored?.split(this.arrayValueSeparator) ?? [];
            return tokens.some((token) => token.includes(normalizedValue));
        }

        return true;
    }
}

export default new QueryBuilder();
